import { FloatingPortal } from "@floating-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import { type ComponentProps, ReactNode, useEffect, useState } from "react";

import { Buttons } from "@/components/atoms/Buttons";
import { ZIndex } from "@/constants";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Typography } from "@/components/ui/typography";

const FLOATING_SELECTION_BAR_BOTTOM_OFFSET_PX = 24;

export type TableDialogSelectionActionsProps = {
  checkedRowIds: string[];
  clearSelection: () => void;
};

/** Which rows a button acts on: those checked, or every row the list matches. */
export type SelectionScope = { allMatching: boolean; count: number };

export type SelectionBarButton = Pick<
  ComponentProps<typeof Button>,
  "variant" | "size" | "disabled" | "className" | "children"
> & {
  onClick: (checkedRows: string[], scope: SelectionScope) => void;
  /** Whether it can act on every row the list matches, not only those loaded. */
  acceptsAllMatching?: boolean;
  /** What to ask before it acts on *scope*, or nothing to act at once. */
  confirm?: (scope: SelectionScope) => string | null;
};

type FloatingSelectionBarProps = {
  visible: boolean;
  nameAsSingular: string;
  numCheckedRows: number;
  allRowsChecked: boolean;
  /** Every row the list matches, loaded or not; offered once all loaded are checked. */
  totalCount?: number;
  summary: string | null;
  onClear: () => void;
  checkedRowIds: string[];
  buttons?: SelectionBarButton[];
  extraActions?: (props: TableDialogSelectionActionsProps) => ReactNode;
};

function FloatingSelectionBar({
  visible,
  nameAsSingular,
  numCheckedRows,
  allRowsChecked,
  totalCount,
  summary,
  onClear,
  checkedRowIds,
  buttons = [],
  extraActions,
}: FloatingSelectionBarProps) {
  // Every row the list matches, not only those loaded: asked for once every
  // loaded row is checked, and let go of when any is unchecked.
  const [allMatching, setAllMatching] = useState(false);
  useEffect(() => {
    if (!allRowsChecked) {
      setAllMatching(false);
    }
  }, [allRowsChecked]);
  const [confirming, setConfirming] = useState<{
    question: string;
    button: SelectionBarButton;
  } | null>(null);

  const moreMatch = totalCount != null && totalCount > numCheckedRows;
  const canSelectAllMatching =
    allRowsChecked &&
    moreMatch &&
    !allMatching &&
    buttons.some((button) => button.acceptsAllMatching);
  const scope: SelectionScope = allMatching
    ? { allMatching: true, count: totalCount ?? numCheckedRows }
    : { allMatching: false, count: numCheckedRows };
  const plural = (count: number) => nameAsSingular + (count > 1 ? "s" : "");
  // What acts on rows not loaded is only what can.
  const shownButtons = allMatching
    ? buttons.filter((button) => button.acceptsAllMatching)
    : buttons;
  const act = (button: SelectionBarButton) => {
    button.onClick(checkedRowIds, scope);
    onClear();
  };

  return (
    <AnimatePresence>
      {visible && (
        <FloatingPortal>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
            style={{
              position: "fixed",
              bottom: FLOATING_SELECTION_BAR_BOTTOM_OFFSET_PX,
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "center",
              pointerEvents: "none",
              zIndex: ZIndex.APP.SELECTION_BAR,
            }}
          >
            <div className="pointer-events-auto bg-popover [border-width:1px] [border-style:solid] border-border p-2 rounded-sm shadow-sm gap-2 flex flex-row items-center flex-nowrap">
              <div className="flex flex-col justify-start pl-2 flex-1 min-w-0">
                <div className="flex flex-row items-center justify-between flex-nowrap gap-4">
                  <Typography level="title-md">
                    {allMatching ? (
                      <>
                        All {scope.count} {plural(scope.count)} selected
                      </>
                    ) : allRowsChecked && !moreMatch ? (
                      <>All rows selected</>
                    ) : (
                      <>
                        {numCheckedRows} {plural(numCheckedRows)} selected
                      </>
                    )}
                  </Typography>
                  {canSelectAllMatching && (
                    <Button
                      variant="ghost"
                      data-testid="selection-bar-select-all-matching"
                      onClick={() => setAllMatching(true)}
                    >
                      Select all {totalCount}
                    </Button>
                  )}
                  <Button variant="secondary" onClick={onClear}>
                    Cancel
                  </Button>
                </div>
                {summary && (
                  <Typography
                    level="body-md"
                    className="text-muted-foreground/80"
                  >
                    {summary}
                  </Typography>
                )}
              </div>
              {((extraActions && !allMatching) || shownButtons.length > 0) && (
                <div className="h-full [border-left-width:1px] [border-left-style:solid] border-border pl-2 shrink-0 flex flex-row items-center gap-2">
                  {!allMatching &&
                    extraActions?.({
                      checkedRowIds,
                      clearSelection: onClear,
                    })}
                  {shownButtons.length > 0 && (
                    <Buttons
                      buttons={shownButtons.map((button) => {
                        // Not a button's: what the bar does with it.
                        const {
                          onClick: _onClick,
                          acceptsAllMatching: _acceptsAllMatching,
                          confirm,
                          ...props
                        } = button;
                        return {
                          ...props,
                          onClick: () => {
                            const question = confirm?.(scope);
                            if (question) {
                              setConfirming({ question, button });
                            } else {
                              act(button);
                            }
                          },
                        };
                      })}
                    />
                  )}
                </div>
              )}
            </div>
            {/* Portalled to the page, over the bar it was asked from. */}
            <AlertDialog
              open={confirming != null}
              onOpenChange={(open) => !open && setConfirming(null)}
            >
              <AlertDialogContent data-testid="selection-bar-confirm">
                <AlertDialogHeader>
                  <AlertDialogTitle>{confirming?.question}</AlertDialogTitle>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    variant={
                      confirming?.button.variant === "destructive"
                        ? "destructive"
                        : "default"
                    }
                    data-testid="selection-bar-confirm-accept"
                    onClick={() => {
                      if (confirming) {
                        act(confirming.button);
                      }
                      setConfirming(null);
                    }}
                  >
                    {confirming?.button.children}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </motion.div>
        </FloatingPortal>
      )}
    </AnimatePresence>
  );
}

export function SelectableListSelectionBar({
  nameAsSingular,
  itemCount,
  getSummary = () => null,
  buttons = [],
  extraActions,
  checkedRows,
  setCheckedRows,
}: {
  nameAsSingular: string;
  /** Total rows in the list; used to detect select-all vs partial selection. */
  itemCount: number;
  getSummary?: (checkedRows: Record<string, boolean>) => string;
  buttons?: SelectionBarButton[];
  extraActions?: (props: TableDialogSelectionActionsProps) => ReactNode;
  checkedRows: Record<string, boolean>;
  setCheckedRows: (checkedRows: Record<string, boolean>) => void;
}) {
  const numCheckedRows = Object.values(checkedRows).filter((row) => row).length;
  const allRowsChecked = itemCount > 0 && numCheckedRows === itemCount;
  const summary = numCheckedRows > 0 ? getSummary(checkedRows) : null;
  const checkedRowIds = Object.keys(checkedRows).filter(
    (key) => checkedRows[key],
  );

  const clearAllCheckedRows = () => {
    setCheckedRows(
      Object.keys(checkedRows).reduce(
        (acc, key) => ({ ...acc, [key]: false }),
        {},
      ),
    );
  };

  return (
    <FloatingSelectionBar
      visible={numCheckedRows > 0}
      nameAsSingular={nameAsSingular}
      numCheckedRows={numCheckedRows}
      allRowsChecked={allRowsChecked}
      summary={summary}
      onClear={clearAllCheckedRows}
      checkedRowIds={checkedRowIds}
      buttons={buttons}
      extraActions={extraActions}
    />
  );
}

export function TableFloatingSelectionBar({
  visible,
  nameAsSingular,
  numCheckedRows,
  allRowsChecked,
  totalCount,
  summary,
  onClear,
  checkedRowIds,
  buttons = [],
  extraActions,
}: FloatingSelectionBarProps) {
  return (
    <FloatingSelectionBar
      visible={visible}
      nameAsSingular={nameAsSingular}
      numCheckedRows={numCheckedRows}
      allRowsChecked={allRowsChecked}
      totalCount={totalCount}
      summary={summary}
      onClear={onClear}
      checkedRowIds={checkedRowIds}
      buttons={buttons}
      extraActions={extraActions}
    />
  );
}
